/**
 * Which side of the app an admin was last using.
 *
 * Admins have two homes — the admin portal and their own teacher dashboard — but
 * only one landing route, so logging back in always dumped them on /admin no matter
 * where they'd been working. This remembers the last side and homeRouteFor honours
 * it.
 *
 * Keyed per-uid so two accounts sharing a device don't inherit each other's side —
 * same convention as SchoolContext's `luc_school_ctx_{uid}`. Survives logout on
 * purpose: logout clears auth state, not device preferences.
 *
 * Only ever consulted for users who actually HAVE admin access; a plain teacher or
 * a student can't be sent somewhere they can't render (see homeRouteFor).
 */

export type Portal = 'admin' | 'teacher';

const keyFor = (uid: string) => `luc_last_portal_${uid}`;

/** Record the side the user is currently on. Silently no-ops when storage is
 *  unavailable (private mode, quota) — a forgotten preference is not an error. */
export const rememberPortal = (uid: string | undefined, portal: Portal): void => {
  if (!uid) return;
  try {
    localStorage.setItem(keyFor(uid), portal);
  } catch {
    /* non-fatal — we just fall back to the default landing route */
  }
};

/** The side this user was last on, or null if unknown/unreadable. */
export const getRememberedPortal = (uid: string | undefined): Portal | null => {
  if (!uid) return null;
  try {
    const v = localStorage.getItem(keyFor(uid));
    return v === 'admin' || v === 'teacher' ? v : null;
  } catch {
    return null;
  }
};
